import { Container, Graphics, InteractionEvent, Sprite, Texture } from 'pixi.js';
import { Game, Camera, EventEmitter } from '.';
import { StageViewport } from '../class';
import { SCENE_STATUS } from '../constant';
import { Boundary, Point } from '../interface';

class Scene extends EventEmitter {
  public readonly game: Game;
  public readonly camera: Camera;
  public readonly container: Container;
  public readonly backgroundContainer: Container;
  public readonly uiContainer: Container;
  public readonly stage: Container;
  public readonly boundary: Boundary<Point>;
  public duration: number = 0;
  public isRunning: boolean = false;
  public isStarted: boolean = false;

  public status: SCENE_STATUS;
  private viewportGraphics?: Graphics;

  constructor(game: Game) {
    super();

    this.game = game;
    this.camera = new Camera({ width: this.game.width, height: this.game.height });
    this.container = new Container();
    this.backgroundContainer = new Container();
    this.uiContainer = new Container();
    this.stage = new Container();
    this.stage.sortableChildren = true;
    this.container.interactive = true;
    this.status = SCENE_STATUS.ACTIVE;
    this.boundary = {
      min: { x: -Infinity, y: -Infinity },
      max: { x: Infinity, y: Infinity },
    };

    if (process.env.NODE_ENV === 'development') {
      this.viewportGraphics = new Graphics();
      this.stage.addChild(this.viewportGraphics);
      const view = new StageViewport();
      view.zIndex = 2;
      this.stage.addChild(view);
    }

    this.camera.setView(this.stage);
    this.camera.setBoundary(this.boundary);

    const touchFrame = new Sprite(Texture.EMPTY);
    touchFrame.width = this.game.width;
    touchFrame.height = this.game.height;

    this.container.addChild(this.backgroundContainer);
    this.container.addChild(touchFrame);
    this.container.addChild(this.stage);
    this.container.addChild(this.uiContainer);

    window.addEventListener('keydown', this._keydown);
    window.addEventListener('keyup', this._keyup);
    this.container.addListener('pointerdown', this._pointerdown);
    this.container.addListener('pointermove', this._pointermove);
    this.container.addListener('pointerup', this._pointerup);

    if (this.game.height / this.game.width > 16 / 9) {
      this.camera.setTargetZoom(this.game.height / 1920, true);
    } else {
      this.camera.setTargetZoom(this.game.width / 1080, true);
    }
  }

  public static preload = async (callback: (currentValue: number, maxValue: number) => void): Promise<void> => {};
  protected pointerdown = (event: InteractionEvent) => {};
  protected pointermove = (event: InteractionEvent) => {};
  protected pointerup = (event: InteractionEvent) => {};
  protected keydown = (event: KeyboardEvent) => {};
  protected keyup = (event: KeyboardEvent) => {};
  protected _pointerdown = (event: InteractionEvent): void => {
    // 모든 Point Event에는 다음과 같은 전 처리가 필요하다.
    if (this.game.app.stage.rotation !== 0) {
      [event.data.global.x, event.data.global.y] = [event.data.global.y, this.game.height - event.data.global.x];
    }
    this.pointerdown(event);
  };
  protected _pointermove = (event: InteractionEvent): void => {
    // 모든 Point Event에는 다음과 같은 전 처리가 필요하다.
    if (this.game.app.stage.rotation !== 0) {
      [event.data.global.x, event.data.global.y] = [event.data.global.y, this.game.height - event.data.global.x];
    }
    this.pointermove(event);
  };
  protected _pointerup = (event: InteractionEvent): void => {
    // 모든 Point Event에는 다음과 같은 전 처리가 필요하다.
    if (this.game.app.stage.rotation !== 0) {
      [event.data.global.x, event.data.global.y] = [event.data.global.y, this.game.height - event.data.global.x];
    }
    this.pointerup(event);
  };
  protected _keydown = (event: KeyboardEvent): void => this.keydown(event);
  protected _keyup = (event: KeyboardEvent): void => this.keyup(event);

  public update(elapsed: number): void {
    this.camera.update(elapsed);

    // Development Mode...
    if (process.env.NODE_ENV === 'development' && this.viewportGraphics) {
      this.viewportGraphics.clear();
      this.viewportGraphics.lineStyle(30, 0xaaaaaa);
      this.viewportGraphics.moveTo(this.camera.viewport[0].x, this.camera.viewport[0].y);
      this.viewportGraphics.lineTo(this.camera.viewport[1].x, this.camera.viewport[1].y);
      this.viewportGraphics.lineTo(this.camera.viewport[2].x, this.camera.viewport[2].y);
      this.viewportGraphics.lineTo(this.camera.viewport[3].x, this.camera.viewport[3].y);
      this.viewportGraphics.lineTo(this.camera.viewport[0].x, this.camera.viewport[0].y);
    }
  }

  public setBoundary(boundary: Boundary<Point>): void {
    this.boundary.min.x = boundary.min.x;
    this.boundary.min.y = boundary.min.y;
    this.boundary.max.x = boundary.max.x;
    this.boundary.max.y = boundary.max.y;
  }

  public destroy(): void {
    super.destroy();

    // Clear Events...
    window.removeEventListener('keydown', this._keydown);
    window.removeEventListener('keyup', this._keyup);
    this.container.removeListener('pointerup', this._pointerup);
    this.container.removeListener('pointerdown', this._pointerdown);

    this.container.removeChild(this.stage);

    this.stage.destroy();
    this.container.destroy();
  }

  public onBegin(): void {}

  public resize(): void {
    this.camera.resize(this.game.width, this.game.height);

    if (this.game.height / this.game.width > 16 / 9) {
      this.camera.setTargetZoom(this.game.height / 1920, true);
    } else {
      this.camera.setTargetZoom(this.game.width / 1080, true);
    }
  }
}

export default Scene;
